import { useState } from "react";
import { AlertMessage, IAlertMessageProps, Spinner } from "component-library";
import { UserClaimTypes, UserManager } from "core";
import { IEmail } from "models";
import { getUserAttribute, UserAttributes } from "shared-auth";

import EmailForm, {
  IEmailForm,
  IEmailFormContent,
} from "pages/Profile/components/common/EmailForm";

import "./ShareViaEmail.scss";

export interface IShareViaEmailContent extends IEmailFormContent {
  emailFormHeader: string;
  emailFormSubmitButton: string;
  emailFormCancelButton: string;
  emailFormSubjectTemplate: string;
}

export interface IShareViaEmailProps {
  content: IShareViaEmailContent;
  sendEmail: (email: IEmail) => Promise<void>;
  closeForm: () => void;
}

const ShareViaEmail = ({
  content,
  sendEmail,
  closeForm,
}: IShareViaEmailProps) => {
  const performerName = UserManager.getClaimWithDefault(
    UserClaimTypes.profileFullName
  );

  const defaultSubject = content.emailFormSubjectTemplate.replace(
    "{performerName}",
    performerName
  );

  const defaultEmailForm: IEmailForm = {
    message: "",
    subject: defaultSubject,
    senderName: getUserAttribute(UserAttributes.FullName) || performerName,
    senderEmail: getUserAttribute(UserAttributes.Email),
    recipients: "",
  };

  const [form, setForm] = useState<IEmailForm>({ ...defaultEmailForm });
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const updateForm = (updatedForm: IEmailForm) => setForm(updatedForm);

  const updateFormValidity = (valid: boolean) => setIsFormValid(valid);

  const convertFormToEmail = (form: IEmailForm): IEmail => {
    const recipients = form.recipients
      .split(",")
      .map((email) => email.trim())
      .filter(Boolean);

    return {
      ...form,
      recipients,
    };
  };

  const handleSendEmail = () => {
    setIsFormSubmitted(true);

    if (isFormValid) {
      setShowLoader(true);
      void (async () => {
        const email = convertFormToEmail(form);
        await sendEmail(email);
        closeForm();
      })();
    }
  };

  const getAlertProps = (
    content: IShareViaEmailContent,
    sendEmail: () => void,
    closeForm: () => void
  ): IAlertMessageProps => {
    return {
      texts: {
        title: content.emailFormHeader,
      },
      buttons: [
        {
          name: content.emailFormSubmitButton,
          type: "primary",
          click: sendEmail,
        },
        {
          name: content.emailFormCancelButton,
          type: "secondary",
          click: closeForm,
        },
      ],
    };
  };

  const alertProps = getAlertProps(content, handleSendEmail, closeForm);

  return (
    <div className="c-share-via-email">
      <AlertMessage {...alertProps}>
        <EmailForm
          updateForm={updateForm}
          updateFormValidity={updateFormValidity}
          initialForm={form}
          content={content}
          isFormSubmitted={isFormSubmitted}
        />
      </AlertMessage>
      {showLoader && (
        <div className="c-share-via-email__spinner">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default ShareViaEmail;
